import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ServiceItemsCard = ({ serviceItems, onUpdate, isEditing, onToggleEdit }) => {
  const [editItems, setEditItems] = useState([...serviceItems]);

  const serviceOptions = [
    { value: 'oil_change', label: 'Ölwechsel' },
    { value: 'brake_service', label: 'Bremsenservice' },
    { value: 'tire_change', label: 'Reifenwechsel' },
    { value: 'inspection', label: 'Inspektion' },
    { value: 'repair', label: 'Reparatur' },
    { value: 'diagnostic', label: 'Diagnose' },
    { value: 'maintenance', label: 'Wartung' },
    { value: 'other', label: 'Sonstiges' }
  ];

  const calculateItemTotal = (item) => {
    const netPrice = parseFloat(item?.price) || 0;
    const quantity = parseInt(item?.quantity) || 1;
    const vatRate = 0.19; // 19% VAT
    
    const netTotal = netPrice * quantity;
    const vatAmount = netTotal * vatRate;
    const grossTotal = netTotal + vatAmount;
    
    return {
      netTotal,
      vatAmount,
      grossTotal
    };
  };

  const calculateTotals = (items) => {
    return items?.reduce((totals, item) => {
      const itemTotals = calculateItemTotal(item);
      return {
        netTotal: totals?.netTotal + itemTotals?.netTotal,
        vatAmount: totals?.vatAmount + itemTotals?.vatAmount,
        grossTotal: totals?.grossTotal + itemTotals?.grossTotal
      };
    }, { netTotal: 0, vatAmount: 0, grossTotal: 0 });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...editItems];
    updatedItems[index] = { ...updatedItems?.[index], [field]: value };
    setEditItems(updatedItems);
  };

  const addItem = () => {
    setEditItems([...editItems, {
      id: Date.now(),
      serviceType: '',
      description: '',
      quantity: 1,
      price: 0,
      unit: 'Stück'
    }]);
  };

  const removeItem = (index) => {
    const updatedItems = editItems?.filter((_, i) => i !== index);
    setEditItems(updatedItems);
  };

  const handleSave = () => {
    onUpdate(editItems);
    onToggleEdit();
  };

  const handleCancel = () => {
    setEditItems([...serviceItems]);
    onToggleEdit();
  };

  const totals = calculateTotals(isEditing ? editItems : serviceItems);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Icon name="Wrench" size={20} />
          Serviceleistungen
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleEdit}
          iconName={isEditing ? "X" : "Edit"}
          iconPosition="left"
        >
          {isEditing ? 'Abbrechen' : 'Bearbeiten'}
        </Button>
      </div>
      <div className="space-y-4">
        {isEditing ? (
          <>
            {editItems?.map((item, index) => (
              <div key={item?.id} className="p-4 border border-border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-text-primary">Position {index + 1}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    iconName="Trash2"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Serviceart"
                    options={serviceOptions}
                    value={item?.serviceType}
                    onChange={(value) => handleItemChange(index, 'serviceType', value)}
                    placeholder="Serviceart auswählen"
                  />
                  
                  <Input
                    label="Beschreibung"
                    value={item?.description}
                    onChange={(e) => handleItemChange(index, 'description', e?.target?.value)}
                    placeholder="Detaillierte Beschreibung"
                  />
                  
                  <Input
                    label="Menge"
                    type="number"
                    value={item?.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e?.target?.value)}
                    min="1"
                  />
                  
                  <Input
                    label="Einzelpreis (netto)"
                    type="number"
                    value={item?.price}
                    onChange={(e) => handleItemChange(index, 'price', e?.target?.value)}
                    placeholder="0,00"
                    step="0.01"
                  />
                </div>
                
                <div className="text-right text-sm text-text-secondary">
                  Gesamt: {calculateItemTotal(item)?.grossTotal?.toLocaleString('de-DE', { 
                    style: 'currency', 
                    currency: 'EUR' 
                  })}
                </div>
              </div>
            ))}
            
            <Button
              variant="outline"
              onClick={addItem}
              iconName="Plus"
              iconPosition="left"
              className="w-full"
            >
              Position hinzufügen
            </Button>
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="default"
                onClick={handleSave}
                iconName="Check"
                iconPosition="left"
              >
                Speichern
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Abbrechen
              </Button>
            </div>
          </>
        ) : (
          <>
            {serviceItems?.map((item, index) => (
              <div key={item?.id} className="p-4 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary">
                      {serviceOptions?.find(opt => opt?.value === item?.serviceType)?.label || item?.serviceType}
                    </h4>
                    <p className="text-text-secondary text-sm mt-1">{item?.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-text-primary">
                      {calculateItemTotal(item)?.grossTotal?.toLocaleString('de-DE', { 
                        style: 'currency', 
                        currency: 'EUR' 
                      })}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {item?.quantity} × {parseFloat(item?.price)?.toLocaleString('de-DE', { 
                        style: 'currency', 
                        currency: 'EUR' 
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Totals */}
        <div className="pt-4 border-t border-border">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Nettobetrag:</span>
              <span className="text-text-primary">
                {totals?.netTotal?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">MwSt. (19%):</span>
              <span className="text-text-primary">
                {totals?.vatAmount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
              <span className="text-text-primary">Gesamtbetrag:</span>
              <span className="text-text-primary">
                {totals?.grossTotal?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceItemsCard;